import React from "react"
import { useSelector } from "react-redux"
import { App } from "#/components/views/app/App"
import { Loading } from "#/components/views/Loading"
import { getUserInfoLoaded } from "#/redux/reducers/userInfo"
import { BenefitEnrollmentProvider } from "#/components/context/enrollment/BenefitEnrollmentContext"
import { DeductibleProvider } from "#/components/context/user/DeductibleContext"
import { MemberPoseWarningMessage } from "../../member-pose/MemberPoseWarningMessage"
import { ConnectInsuranceContextProvider } from "../../context/enrollment/ConnectInsuranceContext"
import { useCurrentUser, UserContextProvider } from "#/components/context/user/UserContext"
import { useBenefitEnrollmentStatus } from "../../context/enrollment/hooks/useBenefitEnrollmentStatus"
import useEmailVerification from "../../EmailVerification/useEmailVerification"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import Settings from "../../../utils/CarrotConfig"
import { useLocaleContext } from "#/components/context/user/LocaleContext"
import { useUserRole } from "../../partner-access/hooks/usePartnerAccess"
import * as Sentry from "@sentry/browser"
import { setHeapPartnerInfo } from "../../../utils/heap"
import { Locale } from "#/types/Locale"
import useBenefit from "#/components/dynamic-content/hooks/useGetBenefit"
import useGetMemberCompany from "#/components/company/hooks/useGetMemberCompany"
import useGetMemberCountry from "#/components/company/hooks/useGetMemberCountry"
import { useSprintsAuthorizationQuery } from "#/services/user/hooks/useSprintsQuery"

function useLaunchDarklyLoaded(): ReturnType<typeof React.useState<boolean>> {
  const { loadLDFlagsForUser } = useCurrentUser()
  const [ldClientLoaded, setLDClientLoaded] = React.useState(false)
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn("Launch darkly failed to load in 3 seconds. Moving on")
      setLDClientLoaded(true)
    }, 3000)
    async function loadLDClient() {
      const loaded = await loadLDFlagsForUser()
      if (loaded) {
        clearTimeout(timeout)
        setLDClientLoaded(true)
      }
    }
    loadLDClient()
    return () => {
      clearTimeout(timeout)
    }
  }, [loadLDFlagsForUser])
  return [ldClientLoaded, setLDClientLoaded]
}

// @ts-expect-error TS(7006) FIXME: Parameter 'props' implicitly has an 'any' type.
function AppLoadingContainer(props): JSX.Element {
  const [launchDarklyLoaded] = useLaunchDarklyLoaded()
  const { initialLoading: benefitEnrollmentStatusLoading } = useBenefitEnrollmentStatus()
  const isUserInfoLoaded = useSelector(getUserInfoLoaded)
  const benefit = useBenefit()
  const memberCompany = useGetMemberCompany()
  const memberCountry = useGetMemberCountry()
  const sprintsAuthorization = useSprintsAuthorizationQuery()
  const { isLoading: emailVerificationInitialLoading } = useEmailVerification()
  const { isUserRoleLoading } = useUserRole()

  const isDataLoaded = [
    !benefitEnrollmentStatusLoading,
    isUserInfoLoaded,
    !sprintsAuthorization.isLoading,
    !benefit.isLoading,
    !memberCompany.isLoading,
    !memberCountry.isLoading,
    launchDarklyLoaded,
    !emailVerificationInitialLoading,
    !isUserRoleLoading
  ].every((i) => i)

  return isDataLoaded ? props.children : <Loading />
}

// @ts-expect-error TS(7006) FIXME: Parameter 'props' implicitly has an 'any' type.
function BlockingAppContext(props): JSX.Element {
  return <UserContextProvider>{props.children}</UserContextProvider>
}

// @ts-expect-error TS(7006) FIXME: Parameter 'props' implicitly has an 'any' type.
function AppContext(props): JSX.Element {
  const { locale: userLocale } = useCurrentUser()
  const { setLocale } = useLocaleContext()
  const memberCountry = useGetMemberCountry()

  React.useEffect(() => {
    const appLocale = userLocale ?? memberCountry?.data?.defaultLocale

    if (setLocale && appLocale) {
      setLocale(Locale.getLocaleFromString(appLocale))
    }
  }, [setLocale, userLocale, memberCountry?.data?.defaultLocale])

  const { isUserRoleLoading, isPartnerAccessingAccount: isPartnerAccount } = useUserRole()
  if (!isUserRoleLoading) {
    setHeapPartnerInfo(isPartnerAccount)
    Sentry?.setContext("Partner Access", {
      isPartnerAccount
    })
  }

  return (
    <DeductibleProvider>
      <BenefitEnrollmentProvider>
        <ConnectInsuranceContextProvider>{props.children}</ConnectInsuranceContextProvider>
      </BenefitEnrollmentProvider>
    </DeductibleProvider>
  )
}

function AppContainer(): JSX.Element {
  return (
    <BlockingAppContext>
      <AppLoadingContainer>
        <AppContext>
          <MemberPoseWarningMessage />
          <App />
          {Settings.SHOW_REACT_QUERY_DEVTOOLS ? (
            <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
          ) : null}
        </AppContext>
      </AppLoadingContainer>
    </BlockingAppContext>
  )
}

export { AppContainer }
